import React, { useEffect, useState } from 'react'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  getPaginationRowModel,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../lib/ui/table'
import useTranslation from '../../shared/TranslationProvider'
import { cn } from '../../../lib/utils'
import { SortIcon } from '~/components/job_search/AlertsPage/icons'
import { columns } from './columns'
import TablePagination from '~/components/job_search/AlertsPage/TablePagination'
import NoSavedAlertsImage from '~/components/job_search/AlertsPage/NoSavedAlertsImage'
import { ExpandedCell } from './ExpandedCell'

const DataTable = ({ feedback, columnFilters, setColumnFilters }) => {
  const [sorting, setSorting] = useState([
    {
      id: 'completed_at',
      desc: true,
    },
  ])
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [pagination])

  const [expanded, setExpanded] = useState({})

  const table = useReactTable({
    debugTable: true,
    data: feedback,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: () => true,
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    onExpandedChange: setExpanded,
    onColumnFiltersChange: setColumnFilters,
    state: {
      pagination,
      sorting,
      expanded,
      columnFilters,
    },
  })
  const { t } = useTranslation()
  const tableHeaderClasses = cn(
    'job-search-table-header h-6 pl-0 text-sm font-bold text-foreground data-[col-id=status]:w-[96px] sm:h-12 sm:px-4 data-[col-id=status]:sm:w-[120px]',
  )

  const showPagination = feedback.length > 10

  const totalPages = table.getPageCount()

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-2xl border border-alto-200">
        <Table tableClass="rounded-2xl">
          <TableHeader className="h-16 rounded-2xl bg-intoo-green-coachcta">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={`${headerGroup.id}-header_group`}
                className="border-alto-200 bg-intoo-green-coachcta p-4 text-white  hover:bg-intoo-green-coachcta sm:px-8 sm:py-6"
              >
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={`${header.id}-header`}
                      className={tableHeaderClasses}
                      data-col-id={header.column.id}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={cn(
                            'flex flex-wrap items-center gap-2',
                            header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                          title={
                            header.column.getCanSort()
                              ? header.column.getNextSortingOrder() === 'asc'
                                ? t('Feedback.sort_ascending')
                                : header.column.getNextSortingOrder() === 'desc'
                                  ? t('Feedback.sort_descending')
                                  : t('Feedback.clear_sort')
                              : undefined
                          }
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}{' '}
                          {header.column.getCanSort() && <SortIcon />}
                        </div>
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table
                .getRowModel()
                .rows.map((row, idx) => (
                  <DataTableRow
                    key={`${row.id}-table_row`}
                    row={row}
                    idx={idx}
                  />
                ))
            ) : (
              <EmptyDataTable />
            )}
          </TableBody>
        </Table>
      </div>
      {showPagination && (
        <TablePagination table={table} totalPages={totalPages} />
      )}
    </div>
  )
}

export default DataTable

const EmptyDataTable = () => {
  const { t } = useTranslation('Feedback')
  return (
    <TableRow>
      <TableCell colSpan={columns.length + 1}>
        <div className="flex flex-col items-center justify-center">
          <NoSavedAlertsImage />
          <div className="max-w-96 text-center">
            <h2 className="p-8">{t('no_feedback_title')}</h2>
            <p className="text-lg">{t('no_feedback')}</p>
            <p className="text-lg">
              <span>{t('visit_outplacement')} </span>
              <span>
                <a href="/coach_services/professional_services">
                  {t('professional_services')}
                </a>
              </span>
              <span> {t('get_started')}</span>
            </p>
          </div>
        </div>
      </TableCell>
    </TableRow>
  )
}

const DataTableRow = ({ row, idx, ...props }) => {
  return (
    <>
      <TableRow
        data-state={row.getIsSelected() && 'selected'}
        fred={5}
        className={cn(
          'border-b-0 hover:bg-transparent',
          idx % 2 === 0 ? 'bg-white' : 'bg-alto-50',
        )}
        expanded={`${row.getIsExpanded()}`}
        {...props}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={`${row.id}_${cell.id}`}
            className="max-w-[29rem] pb-2 pl-0 pr-0 pt-3 sm:p-4 sm:pr-1"
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
      <TableRow
        className={cn(
          'border-b-0',
          idx % 2 === 0 ? 'bg-white' : 'bg-alto-50',
          row.getIsExpanded() ? '' : 'p-0',
        )}
      >
        <TableCell
          colSpan={columns.length + 1}
          className={cn('border-b-0 p-0')}
        >
          <div
            className={cn(
              'transition-all duration-500',
              row.getIsExpanded()
                ? 'visible max-h-screen overflow-scroll md:overflow-hidden'
                : 'invisible max-h-0 overflow-hidden',
            )}
          >
            <ExpandedCell row={row} />
          </div>
        </TableCell>
      </TableRow>
    </>
  )
}
